import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon, 
  CogIcon, 
  BuildingOfficeIcon, 
  PresentationChartLineIcon 
} from '@heroicons/react/24/outline';

const AnalyticsLayout = ({ children }) => {
  const location = useLocation();

  const navigation = [
    {
      name: 'Token Scorecard',
      href: '/analytics/token-scorecard',
      icon: ChartBarIcon,
      description: 'Comprehensive token analysis'
    },
    {
      name: 'Market Makers',
      href: '/analytics/market-makers',
      icon: CogIcon,
      description: 'MM performance tracking'
    },
    {
      name: 'Exchanges',
      href: '/analytics/exchanges',
      icon: BuildingOfficeIcon,
      description: 'Exchange benchmarking'
    },
    {
      name: 'Simulation',
      href: '/analytics/simulation',
      icon: PresentationChartLineIcon,
      description: 'Strategy modeling'
    }
  ];

  const isActive = (path) => location.pathname === path || (location.pathname === '/analytics' && path === '/analytics/token-scorecard');

  return (
    <div className="min-h-screen pt-20 bg-primary-900">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary-800 to-primary-900">
        <div className="section-padding">
          <div className="container-width">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-6 py-2 glass-card text-accent-gold text-sm font-medium tracking-wide uppercase mb-6">
                Analytics Platform
              </div>
              <h1 className="text-display font-serif font-bold text-cream-100 mb-6">
                Real-Time{' '}
                <span className="text-gradient">Analytics Dashboard</span>
              </h1>
              <p className="text-body-lg text-cream-200 max-w-3xl mx-auto">
                Comprehensive monitoring, performance scorecards, and strategic insights 
                for optimal token liquidity management across all market venues.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="section-padding">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="glass-card p-6 sticky top-24">
                <h3 className="text-heading-md font-semibold text-cream-100 mb-6">Analytics Modules</h3>
                <nav className="space-y-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-start p-3 rounded-xl transition-all duration-300 ${
                        isActive(item.href)
                          ? 'bg-accent-blue/20 border border-accent-blue/30'
                          : 'hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <item.icon className={`h-5 w-5 mt-0.5 mr-3 transition-colors duration-300 ${
                        isActive(item.href) ? 'text-accent-blue' : 'text-cream-300 group-hover:text-cream-100'
                      }`} />
                      <div>
                        <div className={`font-medium transition-colors duration-300 ${
                          isActive(item.href) ? 'text-cream-100' : 'text-cream-200 group-hover:text-cream-100'
                        }`}>
                          {item.name}
                        </div>
                        <div className="text-xs text-cream-400 mt-1">
                          {item.description}
                        </div>
                      </div>
                    </Link>
                  ))}
                </nav>
              </div>
            </motion.aside>

            {/* Main Content */}
            <motion.main
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="lg:col-span-3"
            >
              {children}
            </motion.main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsLayout;